import {AfterViewInit, Component, ElementRef, Input, OnInit} from '@angular/core';
import {ProfileService} from 'src/app/auth_profile/services/profile.service';
import {environment} from 'src/environments/environment';
import {ParticipantDef, RoomDef} from '../../models/video.model';
import {switchMap, take, tap} from "rxjs";
import {SpinnerService} from "../../../utils/services/spinner.service";
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {ActivatedRoute} from "@angular/router";
import {TranslateService} from '@ngx-translate/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {V3Person} from "../../../auth_profile/model/v3/people";

declare var bootstrap: any;
const TOOLTIP_TIME = 3000;

@Component({
    selector: 'room-details',
    templateUrl: './room-details.component.html',
    styleUrls: ['./room-details.component.scss'],
    standalone: false
})
export class RoomDetailsComponent implements OnInit, AfterViewInit {
  @Input() room: RoomDef
  @Input()
  self: V3Person
  isCopied = false
  schoolId: number;
  studentId: number;
  tooltipCopyText: string
  tooltipCopiedText: string

  htmlContent: SafeHtml

  constructor(private profile: ProfileService,
              private spinnerService: SpinnerService,
              private authProvider: AuthorizationServiceProvider,
              private route: ActivatedRoute,
              private elRef: ElementRef,
              private translateService: TranslateService,
              private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loadIds()
    this.loadSelfPerson()
    this.getTranslations()
    this.prepareTooltip()
  }

  ngAfterViewInit(): void {
    if (typeof bootstrap === 'undefined') {
      return
    }
    const tooltipEl = this.elRef.nativeElement.querySelector('[data-bs-toggle="tooltip"]');
    if (tooltipEl) {
      new bootstrap.Tooltip(tooltipEl);
    }
  }

  loadIds() {
    this.authProvider.getAuthDetailsService().pipe(
      switchMap(service => service.getSelfSchoolId()),
      tap(schoolId => {
        this.schoolId = schoolId
      }),
      switchMap(_ => this.route.paramMap),
      tap( params => {
        this.schoolId = Number(params.get('schoolId'))
        this.studentId = Number(params.get('studentId'))
      })
    ).subscribe()
  }

  loadSelfPerson() {
    this.spinnerService.trace(
      this.profile.getSelfPerson().pipe(take(1))
    ).subscribe({
      next: self => this.self = self,
      error: () => {}
    })
  }

  getScheduleName() {
    return this.room?.name
  }

  getScheduleDateCreated() {
    return new Date(this.room?.createDate)
    .toLocaleString('en-uk',
      { day: '2-digit', month: 'short', year: 'numeric',
        hour: 'numeric', minute: 'numeric' })
  }

  getScheduleDatePlanned() {
    if(this.room)
      return new Date(this.room?.plannedDate)
      .toLocaleString('en-uk',
        { day: '2-digit', month: 'short', year: 'numeric',
          hour: 'numeric', minute: 'numeric' })
    else
      return ''
  }

  getScheduleDuration() {
    return this.getRoomDuration(this.room?.provisioning?.duration || 0)
  }

  getTeacher() {
    return this.room?.participants
    ?.find(participant => participant?.role === 'Teacher')
  }

  getInitials(parti: ParticipantDef) {
    return (parti?.name ?? '')
      .trim()
      .split(/\s+/)
      .filter(part => part.length > 0)
      .map(part => part[0])
      .join('')
      .toUpperCase()
  }

  getName(parti: ParticipantDef) {
    return parti?.name
  }

  getScheduleState() {
    return this.room?.provisioning?.status ? `LESSONS.LESSON_STATUS.${this.room.provisioning.status}` : null
  }

  getRoomDuration(time: number) {
    return (time/60).toFixed(0)
  }

  copyToClipboard(link: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = link;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.isCopied = true

    setTimeout(() => {
      this.isCopied = false;
    }, TOOLTIP_TIME);
  }

  createLink(parti: ParticipantDef): string {
    if (!parti) {
      return '';
    }
    const origin = environment.videoWebBase;
    if(parti?.uuid)
      if (parti.role === 'Teacher') {
        return `${origin}/participants/${parti.uuid}/teacher-room`;
      } else {
        return `${origin}/participants/${parti.uuid}/student-room`;
      }
    else
      return null
  }

  getSelf() {
    const participants = this.room?.participants
    if (!participants) {
      return undefined
    }
    if (this.studentId) {
      const byId = participants.find(parti => parti?.eid === `casa:${this.studentId}`)
      if (byId) {
        return byId
      }
    }
    // Fallback: whitespace-normalized name match (handles missing eid).
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
    const name = this.self?.details?.name
    const surname = this.self?.details?.surname
    if (!name && !surname) {
      return undefined
    }
    const selfName = normalize(`${name ?? ''} ${surname ?? ''}`)
    return participants.find(parti => normalize(parti?.name) === selfName)
  }

  getSelfLink(): string {
    return this.createLink(this.getSelf())
  }

  enterRoom() {
    const link = this.getSelfLink()
    if (!link) {
      return
    }
    window.open(link, '_blank');
  }

  copySelfLink() {
    const link = this.getSelfLink()
    if (!link) {
      return
    }
    this.copyToClipboard(link)
  }

  getTranslations() {
    this.translateService.get('COL.LESSONS.HOW_TO_JOIN_LESSON.MODAL.BODY').subscribe(
      (value) => this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(value)
    )
    this.translateService.get('BUTTON.COPY_LINK').subscribe(
      (value) => this.tooltipCopyText = value
    )
    this.translateService.get('LESSONS.ROOM_DETAILS.LINK_COPIED').subscribe(
      (value) => this.tooltipCopiedText = value
    )
  }

  prepareTooltip() {
    if (typeof bootstrap === 'undefined') {
      return
    }
    const tooltipEl = document.getElementById('copyTooltip');
    if (!tooltipEl) {
      return
    }
    const tooltip = new bootstrap.Tooltip(tooltipEl);

    tooltipEl.addEventListener('mouseenter', () => {
      this.setTooltipTitle(tooltipEl);
      tooltip.show();
    })

    tooltipEl.addEventListener('click', () => {
      this.setTooltipTitle(tooltipEl);
      tooltip.show();
      setTimeout(() => {
        tooltip.hide();
      }, TOOLTIP_TIME);
    })

    tooltipEl.addEventListener('mouseleave', () => {
      if(!this.isCopied) tooltip.hide();
    })
  }

  setTooltipTitle(tooltipEl: HTMLElement) {
    tooltipEl.setAttribute('data-bs-original-title', this.isCopied ? this.tooltipCopiedText : this.tooltipCopyText);
    if(this.isCopied) {
      setTimeout(() => {
        tooltipEl.setAttribute('data-bs-original-title', this.tooltipCopyText);
      }, TOOLTIP_TIME);
    }
  }

  getMobileCopyButtonText() {
    return this.isCopied ? 'LESSONS.ROOM_DETAILS.LINK_COPIED' : 'LESSONS.ROOM_DETAILS.OR_COPY_LINK'
  }
}
